import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  useFetchAllQuizzesMutation,
  useChangeQuizStatusMutation,
  useGetQuizResultsMutation,
  useFetchQuizByIdMutation,
} from "../../../features/tenant/quiz/quizApi";
import { AiOutlineEdit, AiOutlineEye } from "react-icons/ai";
import { HiOutlinePlus } from "react-icons/hi";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";

const QuizList = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  const [filterTitle, setFilterTitle] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [resultsData, setResultsData] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [selectedQuizTitle, setSelectedQuizTitle] = useState("");

  const [fetchAllQuizzes, { isLoading: isFetchingQuizzes }] = useFetchAllQuizzesMutation();
  const [changeQuizStatus] = useChangeQuizStatusMutation();
  const [getQuizResults, { isLoading: isFetchingResults }] = useGetQuizResultsMutation();
  const [fetchQuizById, { isLoading: isFetchingQuiz }] = useFetchQuizByIdMutation();

  const fetchQuizzesData = () => {
    fetchAllQuizzes({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: { 
        limit, 
        offset,
        title: filterTitle || undefined,
        status: filterStatus || undefined
      },
    })
      .unwrap()
      .then((res) => {
        setQuizzes(res?.body?.quizzes || []);
        setTotalRows(res?.body?.total_quizzes || 0);
      })
      .catch((err) => {
        toast.error("Failed to fetch quizzes");
      });
  };

  useEffect(() => {
    fetchQuizzesData();
  }, [limit, offset]);

  const handleStatusToggle = async (quiz) => {
    // Status Codes: 1 (Active), 2 (Inactive), 3 (Draft)
    // Toggle between 1 and 2 for simplicity
    const newStatus = quiz.status === "1" ? "2" : "1";
    try {
      await changeQuizStatus({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        quiz_id: quiz.id,
        body: { status: newStatus },
      }).unwrap();
      toast.success("Status updated");
      fetchQuizzesData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleViewResults = (quiz) => {
    setSelectedQuizTitle(quiz.title);
    getQuizResults({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      quiz_id: quiz.id,
    })
      .unwrap()
      .then((res) => {
        setResultsData(res?.body?.results || []);
        setShowResultsModal(true);
      })
      .catch((err) => {
        toast.error("Failed to fetch results");
      });
  };

  const handleViewQuiz = (quiz) => {
    fetchQuizById({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      id: quiz.id,
    })
      .unwrap()
      .then((res) => {
        setViewData(res?.body || null);
        setShowViewModal(true);
      })
      .catch((err) => {
        toast.error("Failed to fetch quiz details");
      });
  };

  const columns = [
    {
      name: "SNo",
      cell: (row, idx) => offset + idx + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Title",
      cell: (row) => row.title,
      sortable: true,
    },
    {
      name: "Total Questions",
      cell: (row) => row.total_questions,
      sortable: true,
      width: "150px",
    },
    {
      name: "Max Attempts",
      cell: (row) => row.max_attempts,
      sortable: true,
      width: "120px",
    },
    {
      name: "Reward Available",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-bold ${row.reward_available ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
          {row.reward_available ? "Yes" : "No"}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Reward Points",
      cell: (row) => row.reward_points || 0,
      sortable: true,
      width: "130px",
    },
    {
      name: "Status",
      cell: (row) => (
        <button
          onClick={() => handleStatusToggle(row)}
          className={`px-3 py-1 rounded-full text-xs font-bold ${
            row.status === "1" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}
        >
          {row.status === "1" ? "Active" : "Inactive"}
        </button>
      ),
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewQuiz(row)}
            className="flex items-center space-x-1 bg-blue-500 text-white px-2 py-1 rounded text-xs"
            title="View Details"
          >
            <AiOutlineEye /> <span>View</span>
          </button>
          <button
            onClick={() => navigate("add", { state: row })}
            className="flex items-center space-x-1 bg-primary text-white px-2 py-1 rounded text-xs"
            title="Edit Quiz"
          >
            <AiOutlineEdit /> <span>Edit</span>
          </button>
          <button
            onClick={() => handleViewResults(row)}
            className="flex items-center space-x-1 bg-secondary text-white px-2 py-1 rounded text-xs"
            title="View Results"
          >
            <AiOutlineEye /> <span>Results</span>
          </button>
        </div>
      ),
      width: "180px",
    },
  ];

  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Quiz Management
            <LinksComponent />
          </div>
          <div className="p-3">
            <div className="bg-white shadow rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search Title</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter quiz title..."
                    value={filterTitle}
                    onChange={(e) => setFilterTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                  </select>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setOffset(0);
                      fetchQuizzesData();
                    }}
                    className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-opacity-90 transition"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setFilterTitle("");
                      setFilterStatus("");
                      setOffset(0);
                      fetchAllQuizzes({
                        token: sessionStorage.getItem("access_token"),
                        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                        body: { limit, offset: 0 },
                      })
                        .unwrap()
                        .then((res) => {
                          setQuizzes(res?.body?.quizzes || []);
                          setTotalRows(res?.body?.total_quizzes || 0);
                        });
                    }}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-300 transition"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-3">
              {isFetchingQuizzes && <CircleLoaderComponent />}
              <DataTableComponent
                keyField={(row) => row.id}
                columns={columns}
                data={quizzes}
                totalRows={totalRows}
                handlePerRowsChange={(newLimit) => setLimit(newLimit)}
                handlePageChange={(page) => setOffset((page - 1) * limit)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer">
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button
            onClick={() => navigate("add")}
            className="inline-flex justify-center w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none"
            style={{ backgroundColor: primaryColor["700"] }}
          >
            <HiOutlinePlus className="w-full h-full p-3" />
          </Menu.Button>
        </Menu>
      </div>

      {showResultsModal && (
        <ModalComponent
          size="5xl"
          show={showResultsModal}
          setShow={setShowResultsModal}
        >
          <div className="max-h-[85vh] overflow-y-auto p-4">
            <h2 className="text-xl font-bold mb-4 border-b pb-2">Results for: {selectedQuizTitle}</h2>
            {isFetchingResults ? (
              <CircleLoaderComponent />
            ) : resultsData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Id</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Mobile</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempted At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {resultsData.map((result, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{result.user_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.mobile}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.user_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.score} / {result.total_questions * result.points_per_question}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(result.created_at).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            result.passed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}>
                            {result.passed ? "Passed" : "Failed"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">No attempts found for this quiz.</div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowResultsModal(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </ModalComponent>
      )}

      {showViewModal && viewData && (
        <ModalComponent
          size="5xl"
          show={showViewModal}
          setShow={setShowViewModal}
        >
          <div className="max-h-[85vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start border-b pb-4 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{viewData.title}</h2>
                <p className="text-gray-600 mt-1">{viewData.description}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                viewData.status === "1" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}>
                {viewData.status === "1" ? "Active" : "Inactive"}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase">Total Questions</p>
                <p className="text-lg font-bold text-gray-800">{viewData.total_questions}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase">Total Points</p>
                <p className="text-lg font-bold text-gray-800">{viewData.total_points}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase">Passing Marks</p>
                <p className="text-lg font-bold text-gray-800">{viewData.passing_marks_required ? viewData.passing_marks : "Not Required"}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase">Max Attempts</p>
                <p className="text-lg font-bold text-gray-800">{viewData.max_attempts}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase">Reward Available</p>
                <p className="text-lg font-bold text-gray-800">{viewData.reward_available ? "Yes" : "No"}</p>
              </div>
              {viewData.reward_available && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500 font-medium uppercase">Reward Points</p>
                  <p className="text-lg font-bold text-gray-800">{viewData.reward_points}</p>
                </div>
              )}
            </div>

            <h3 className="text-lg font-bold mb-4 border-l-4 border-primary pl-3">Questions & Options</h3>
            <div className="space-y-4 pr-2">
              {viewData.questions?.map((q, idx) => (
                <div key={idx} className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <p className="font-semibold text-gray-800">{q.question}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-9">
                    {["a", "b", "c", "d"].map((opt) => (
                      <div 
                        key={opt}
                        className={`p-2 rounded-lg border text-sm flex justify-between items-center ${
                          q.correct_option.toLowerCase() === opt 
                            ? "bg-green-50 border-green-200 text-green-700 font-medium" 
                            : "bg-gray-50 border-gray-100 text-gray-600"
                        }`}
                      >
                        <span>{opt.toUpperCase()}. {q[`option_${opt}`]}</span>
                        {q.correct_option.toLowerCase() === opt && (
                          <span className="bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded uppercase">Correct</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-8 pt-4 border-t">
              <button
                onClick={() => setShowViewModal(false)}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-bold hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default QuizList;
